<script setup>
import { inject, computed, ref, nextTick, watch } from "vue";
import {
  IconPhone,
  IconPhoneOff,
  IconMicrophone,
  IconMicrophoneOff,
  IconPlayerPlay,
  IconDialpad,
  IconPhoneCall,
  IconX,
} from "@tabler/icons-vue";

const jssip = inject("jssip", null);

const callState = computed(() => jssip?.callState?.value ?? "idle");
const callSource = computed(() => jssip?.callSource?.value ?? null);
const remoteNumber = computed(() => jssip?.remoteNumber?.value ?? "");
const formattedDuration = computed(
  () => jssip?.formattedDuration?.value ?? "00:00"
);
const isMuted = computed(() => jssip?.isMuted?.value ?? false);
const isOnHold = computed(() => jssip?.isOnHold?.value ?? false);

const endCall = jssip?.endCall;
const answerCall = jssip?.answerCall;
const toggleMute = jssip?.toggleMute;
const toggleHold = jssip?.toggleHold;
const transferCall = jssip?.transferCall;

const callDirection = computed(() => jssip?.callDirection?.value ?? null);

const showTransferInput = ref(false);
const transferTarget = ref("");
const transferInputRef = ref(null);

const openTransfer = async () => {
  transferTarget.value = "";
  showTransferInput.value = true;
  await nextTick();
  transferInputRef.value?.focus?.();
};

const cancelTransfer = () => {
  showTransferInput.value = false;
  transferTarget.value = "";
};

const submitTransfer = () => {
  const target = transferTarget.value.trim();
  if (!target) return;
  const ok = transferCall?.(target);
  if (ok !== false) {
    showTransferInput.value = false;
    transferTarget.value = "";
  }
};

// Auto-close the transfer input if the call leaves "connected".
watch(callState, (state) => {
  if (state !== "connected" && showTransferInput.value) {
    showTransferInput.value = false;
    transferTarget.value = "";
  }
});

// Show for dialpad + inbound (not lead outbound — CallDialog has that UI). Allow any non-"lead" source so inbound still shows if callSource is unset.
const isVisible = computed(() => {
  const state = callState.value;
  const isActiveCall = ["ringing", "connecting", "connected"].includes(state);
  if (!isActiveCall) return false;
  if (callSource.value === "lead") return false;
  return true;
});

const statusText = computed(() => {
  const state = callState.value;
  if (isOnHold.value) return "Call Hold";
  if (state === "connecting") return "Calling...";
  if (state === "ringing")
    return callDirection.value === "incoming" ? "Incoming call…" : "Ringing…";
  if (state === "connected") return "Call In Progress";
  return "";
});

const showTimer = computed(
  () => callState.value === "connected" && !isOnHold.value
);

const displayNumber = computed(() => {
  const num = remoteNumber.value;
  if (!num) return "";
  return num.startsWith("+") ? num : `+${num}`;
});

/** Blind transfer (REFER) is for outbound / dialpad legs only, not true inbound. */
const showTransferControls = computed(
  () =>
    callState.value === "connected" &&
    callDirection.value !== "incoming" &&
    transferCall
);

watch(
  callDirection,
  (dir) => {
    if (dir === "incoming" && showTransferInput.value) cancelTransfer();
  }
);
</script>

<template>
  <Transition name="slide-up">
    <div
      v-if="isVisible && jssip"
      class="vf-fixed vf-bottom-6 vf-left-1/2 -vf-translate-x-1/2 vf-z-[9999] vf-flex vf-items-center vf-gap-4 vf-rounded-xl vf-bg-neutral-800 vf-px-5 vf-py-4 vf-shadow-lg"
    >
      <!-- Call info -->
      <div class="vf-flex vf-items-center vf-gap-4">
        <div
          class="vf-flex vf-h-12 vf-w-12 vf-items-center vf-justify-center vf-rounded-full vf-bg-primary-500"
        >
          <IconPhone size="24" class="vf-text-white" stroke-width="2" />
        </div>
        <div class="vf-flex vf-flex-col">
          <span class="vf-font-semibold vf-text-white">
            {{ displayNumber || "Unknown" }}
          </span>
          <span class="vf-text-sm vf-text-neutral-400">{{ statusText }}</span>
        </div>
      </div>

      <!-- Separator -->
      <div
        class="vf-h-10 vf-w-px vf-shrink-0 vf-bg-neutral-600"
        aria-hidden="true"
      />

      <!-- Call controls -->
      <div class="vf-flex vf-items-center vf-gap-2">
        <!-- Timer -->
        <div
          v-if="showTimer"
          class="vf-flex vf-items-center vf-gap-1.5 vf-pr-2 vf-text-white"
        >
          <span
            class="vf-h-2 vf-w-2 vf-rounded-full vf-bg-success-500"
            aria-hidden="true"
          />
          <span class="vf-text-sm vf-font-medium">{{ formattedDuration }}</span>
        </div>

        <!-- Answer (inbound INVITE — same UA as lead/outbound calls) -->
        <button
          v-if="callState === 'ringing' && callDirection === 'incoming'"
          type="button"
          class="vf-flex vf-h-12 vf-w-12 vf-items-center vf-justify-center vf-rounded-full vf-bg-success-500 vf-text-white vf-transition-colors hover:vf-bg-success-600"
          @click="answerCall?.()"
          title="Answer"
        >
          <IconPhone size="24" stroke-width="2" />
        </button>

        <!-- Mute -->
        <button
          v-if="callState === 'connected'"
          type="button"
          class="vf-flex vf-h-10 vf-w-10 vf-items-center vf-justify-center vf-rounded-lg vf-transition-colors"
          :class="
            isMuted
              ? 'vf-bg-danger-500/20 vf-text-danger-400'
              : 'vf-bg-neutral-700 vf-text-white hover:vf-bg-neutral-600'
          "
          @click="toggleMute?.()"
          :title="isMuted ? 'Unmute' : 'Mute'"
        >
          <IconMicrophoneOff v-if="isMuted" size="20" />
          <IconMicrophone v-else size="20" />
        </button>

        <!-- Hold / Keypad -->
        <button
          v-if="callState === 'connected'"
          type="button"
          class="vf-flex vf-h-10 vf-w-10 vf-items-center vf-justify-center vf-rounded-lg vf-transition-colors"
          :class="
            isOnHold
              ? 'vf-bg-amber-500/30 vf-text-amber-400 hover:vf-bg-amber-500/40'
              : 'vf-bg-neutral-700 vf-text-white hover:vf-bg-neutral-600'
          "
          @click="toggleHold?.()"
          :title="isOnHold ? 'Resume' : 'Hold'"
        >
          <IconPlayerPlay v-if="isOnHold" size="20" />
          <IconDialpad v-else size="20" />
        </button>

        <!-- Blind transfer (SIP REFER) — not offered for genuine inbound calls -->
        <button
          v-if="showTransferControls"
          type="button"
          class="vf-flex vf-h-10 vf-w-10 vf-items-center vf-justify-center vf-rounded-lg vf-transition-colors"
          :class="
            showTransferInput
              ? 'vf-bg-primary-500/30 vf-text-primary-300'
              : 'vf-bg-neutral-700 vf-text-white hover:vf-bg-neutral-600'
          "
          @click="showTransferInput ? cancelTransfer() : openTransfer()"
          title="Transfer call"
        >
          <IconPhoneCall size="20" />
        </button>

        <!-- Hang up -->
        <button
          type="button"
          class="vf-flex vf-h-12 vf-w-12 vf-items-center vf-justify-center vf-rounded-full vf-bg-danger-500 vf-text-white vf-transition-colors hover:vf-bg-danger-600"
          @click="endCall?.()"
          title="End call"
        >
          <IconPhoneOff size="24" stroke-width="2" />
        </button>
      </div>

      <!-- Transfer target input (inline, above the bar) -->
      <Transition name="fade-up">
        <div
          v-if="showTransferInput && showTransferControls"
          class="vf-absolute vf-left-0 vf-right-0 vf-bottom-full vf-mb-2 vf-flex vf-items-center vf-gap-2 vf-rounded-xl vf-bg-neutral-800 vf-px-4 vf-py-3 vf-shadow-lg"
        >
          <label
            class="vf-shrink-0 vf-text-sm vf-font-medium vf-text-neutral-300"
          >
            Transfer to:
          </label>
          <input
            ref="transferInputRef"
            v-model="transferTarget"
            type="text"
            inputmode="tel"
            placeholder="Number or SIP URI"
            class="vf-flex-1 vf-min-w-0 vf-rounded-md vf-bg-neutral-700 vf-px-3 vf-py-1.5 vf-text-sm vf-text-white vf-placeholder-neutral-500 focus:vf-outline-none focus:vf-ring-1 focus:vf-ring-primary-500"
            @keydown.enter.prevent="submitTransfer"
            @keydown.esc.prevent="cancelTransfer"
          />
          <button
            type="button"
            class="vf-flex vf-h-8 vf-w-8 vf-items-center vf-justify-center vf-rounded-md vf-bg-primary-500 vf-text-white vf-transition-colors hover:vf-bg-primary-600 disabled:vf-opacity-50 disabled:hover:vf-bg-primary-500"
            :disabled="!transferTarget.trim()"
            @click="submitTransfer"
            title="Send transfer"
          >
            <IconPhoneCall size="16" />
          </button>
          <button
            type="button"
            class="vf-flex vf-h-8 vf-w-8 vf-items-center vf-justify-center vf-rounded-md vf-bg-neutral-700 vf-text-neutral-300 vf-transition-colors hover:vf-bg-neutral-600"
            @click="cancelTransfer"
            title="Cancel transfer"
          >
            <IconX size="16" />
          </button>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.2s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translate(-50%, 1rem);
}
.slide-up-enter-to,
.slide-up-leave-from {
  opacity: 1;
  transform: translate(-50%, 0);
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition: all 0.15s ease;
}
.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(0.5rem);
}
.fade-up-enter-to,
.fade-up-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
